import { useCallback } from 'react';
import type { CustomPanelControls } from '@practium/ui';
import SortByAlphaOutlined from '@mui/icons-material/SortByAlphaOutlined';
import { Button, FilterChip, Pagination, PeriodPanel, SearchBar } from '@practium/ui';

import styles from './ProductRequestsPage.module.css';
import { ProductRequestModals } from '../components/Modals/ProductRequestModals';
import { ProductRequestsTable } from '../components/Tables/ProductRequestsTable';
import { ProductRequestsPageSkeleton } from '../components/Skeletons/ProductRequestsPageSkeleton';
import { useProductRequests, FILTER_GROUPS } from '../hooks/useProductRequest';
import { Add } from '@mui/icons-material';

const TABS = [
	{ key: 'pending' as const, label: 'Pendentes' },
	{ key: 'approved' as const, label: 'Aceitos' },
	{ key: 'rejected' as const, label: 'Rejeitados' },
	{ key: 'all' as const, label: 'Todos' },
];

export const ProductRequestsPage = () => {
	const {
		isLoading,
		activeTab,
		setActiveTab,
		selectedRequest,
		setSelectedRequest,
		isCreateOpen,
		formData,
		setField,
		formErrors,
		clearError,
		filterValues,
		setFilterValues,
		customDateFrom,
		customDateTo,
		handleCustomDateChange,
		searchQuery,
		setSearchQuery,
		sortAsc,
		setSortAsc,
		page,
		setPage,
		paginatedRequests,
		totalPages,
		currentDate,
		userName,
		handleOpenCreate,
		handleCloseCreate,
		handleSubmit,
	} = useProductRequests();

	const renderPeriodPanel = useCallback(
		({ close, activate }: CustomPanelControls) => (
			<PeriodPanel
				dateFrom={customDateFrom}
				dateTo={customDateTo}
				onChange={(from, to) => {
					handleCustomDateChange(from, to);
					activate();
				}}
				onClose={close}
			/>
		),
		[customDateFrom, customDateTo, handleCustomDateChange]
	);

	if (isLoading) return <ProductRequestsPageSkeleton />;

	return (
		<div className={styles.page}>
			<div className={styles.contentWrapper}>
				<div className={styles.pageHeader}>
					<div className={styles.pageHeaderLeft}>
						<h1 className={styles.pageTitle}>Solicitações de Produtos</h1>
						<p className={styles.pageSubtitle}>
							Solicite os produtos para serem cadastrados no sistema
						</p>
					</div>
					<div className={styles.pageHeaderRight}>
						<Button variant="primary" icon={<Add className={styles.addIcon} />} className={styles.solicitarBtn} onClick={handleOpenCreate}>
							Solicitar
						</Button>
					</div>
				</div>

				<div className={styles.card}>
					<div className={styles.tabsWrapper}>
						{TABS.map((tab) => (
							<button
								key={tab.key}
								className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
								onClick={() => setActiveTab(tab.key)}
							>
								{tab.label}
							</button>
						))}
					</div>

					<div className={styles.filterRow}>
						<div className={styles.searchWrapper}>
							<SearchBar
								value={searchQuery}
								onChange={setSearchQuery}
								placeholder="Buscar por nome ou fabricante..."
							/>
						</div>
						<button
							type="button"
							className={`${styles.sortBtn} ${sortAsc ? styles.sortBtnActive : ''}`}
							onClick={() => setSortAsc((v) => !v)}
							title="Ordenar em ordem alfabética"
						>
							<SortByAlphaOutlined />
						</button>
						<FilterChip
							values={filterValues}
							onChange={setFilterValues}
							groups={FILTER_GROUPS}
							placeholder="Filtros"
							customPanels={{ 'periodo:custom': renderPeriodPanel }}
						/>
					</div>

					<div className={styles.tableContent}>
						<ProductRequestsTable requests={paginatedRequests} onRowClick={setSelectedRequest} />
					</div>

					<div className={styles.paginationRow}>
						<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
					</div>
				</div>

				<ProductRequestModals
					selectedRequest={selectedRequest}
					onCloseDetail={() => setSelectedRequest(null)}
					isCreateOpen={isCreateOpen}
					onCloseCreate={handleCloseCreate}
					onSubmit={handleSubmit}
					formData={formData}
					onFieldChange={setField}
					formErrors={formErrors}
					onClearError={clearError}
					currentDate={currentDate}
					userName={userName}
				/>
			</div>
		</div>
	);
};
